import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Typography, Button, Stack, Tooltip, Grid, CircularProgress } from '@mui/material';
import ACHAccount from './ACHAccount';
import ACHEditAccount from './ACHEditAccount';
import { FlexAlerts, AlertSeverityTypes } from '../Message/Alerts/Alerts';
import { DataPostApiV2, DataGetApiV2, FlexAPIHeaders } from '../../helpers/PostDataApi';
import config from '../../constants/config';
import InfoTooltip from '../InfoTooltip/InfoTooltip';

const ACHPaymentInfo = (props) => {
  const { customerId } = props;
  const [paymentGateway, setPaymentGateway] = React.useState('paytrace');
  const [paymentMethods, setPaymentMethods] = React.useState([]);
  const [isAddMode, setIsAddMode] = React.useState(false);
  const [isLoading] = React.useState(false);
  const [messages, setMessages] = React.useState([]);

  React.useEffect(() => {
    getAccounts();
  }, [customerId]); // dependency is whatever is used to decide what is passed back from API

  React.useEffect(() => {
    const locationDetails = JSON.parse(localStorage.getItem('locationPaydetails'));
    setPaymentGateway(locationDetails?.gateway);
  }, []);

  const getAccounts = () => {
    if (customerId && customerId !== '') {
      const query = {
        customerId: customerId,
      };

      DataGetApiV2(config.BASEURL + 'getPaymentVault', query)
        .then((response) => {
          if (response.data?.paymentMethods) {
            setPaymentMethods(response.data.paymentMethods);
          }
        })
        .catch((_error) => {
          // errors on finding data rather than it not existing.
          setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not get account data.' }]);
        });
    }
  };

  const updateAccount = (newAccountData) => {
    setMessages([]);
    const originalPaymentMethods = [...paymentMethods];

    const payload = { ach: { ...newAccountData } };

    const query = { customerId: customerId, paymentMethodId: newAccountData.paymentMethodId };

    // Optimistic UI
    setMessages([{ severity: AlertSeverityTypes.warning, message: 'Processing account details.' }]);
    let updatedPaymentMethods = [...paymentMethods];
    let index = updatedPaymentMethods.findIndex((method) => method.paymentMethodId === newAccountData.paymentMethodId);
    updatedPaymentMethods[index] = newAccountData;
    setPaymentMethods(updatedPaymentMethods);

    DataPostApiV2(config.BASEURL + 'updatePaymentMethodInVault', payload, query)
      .then((response) => {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Account updated successfully.' }]);
        updatedPaymentMethods = [...paymentMethods];
        index = updatedPaymentMethods.findIndex((method) => method.paymentMethodId === response.data.paymentMethodId);
        updatedPaymentMethods[index] = response.data;
        setPaymentMethods(updatedPaymentMethods);
        getAccounts();
      })
      .catch((_error) => {
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not update account.' }]);
        setPaymentMethods(originalPaymentMethods); // remove any changes made for optimistic UI
      });
  };

  const deleteAccount = (paymentMethodId) => {
    setMessages([]);
    const originalPaymentMethods = [...paymentMethods];

    const query = { customerId: customerId, paymentMethodId: paymentMethodId };

    // Optimistic UI
    setMessages([{ severity: AlertSeverityTypes.warning, message: 'Processing account details.' }]);
    let updatedPaymentMethods = paymentMethods.filter((method) => method.paymentMethodId !== paymentMethodId);
    setPaymentMethods(updatedPaymentMethods);

    axios
      .delete(config.BASEURL + 'deletePaymentMethodFromVault', { headers: FlexAPIHeaders(), params: query })
      .then((response) => {
        let updatedPaymentMethods = paymentMethods.filter(
          (method) => method.paymentMethodId !== response.data.paymentMethodId
        );
        setPaymentMethods(updatedPaymentMethods);
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Account deleted successfully.' }]);
        getAccounts();
      })
      .catch((_error) => {
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not delete account.' }]);
        setPaymentMethods(originalPaymentMethods); // remove any changes for optimistic UI
      });
  };

  const addAccount = (newAccountData) => {
    setMessages([]);
    const originalPaymentMethods = [...paymentMethods];

    // validate new data - what fields are required
    // should pass data back to customer creation/editting if customer id doesnt exist? Customer needs to be created in gateway with all info?
    // what information is required to create in gateway and if we create in gateway, how do we then create let customer know what its id is?

    const payload = { ach: newAccountData };

    const query = { customerId: customerId };

    setMessages([{ severity: AlertSeverityTypes.warning, message: 'Processing account details.' }]);
    setIsAddMode(false);
    let newAccount = { ...newAccountData, paymentType: 'ACH' };
    setPaymentMethods((prev) => [...prev, newAccount]);

    DataPostApiV2(config.BASEURL + 'addPaymentMethodToVault', payload, query)
      .then((response) => {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Account added successfully.' }]);
        let newAccount = { ...response.data };
        setPaymentMethods([...originalPaymentMethods, newAccount]);

        getAccounts();
      })
      .catch((error) => {
        setMessages([
          { severity: AlertSeverityTypes.error, message: error.response?.data?.message ?? 'Could not add account.' },
        ]);
        setPaymentMethods(originalPaymentMethods); // remove any changes for optimistic UI
      });
  };

  const handleClickAddAccount = () => {
    setMessages([]);
    setIsAddMode(true);
  };

  return (
    <Grid container spacing={1}>
      <Grid container item justifyContent={'flex-start'} columnSpacing={1}>
        <Grid item md={'auto'}>
          <Typography component="h2">ACH Payment Info</Typography>
        </Grid>
        <Grid item md={'auto'}>
          <InfoTooltip message="If you need to change the account number or routing number, please delete the account and add a new one." />
        </Grid>
      </Grid>
      {messages.length > 0 && (
        <Grid item xs={12} md={12}>
          <FlexAlerts messages={messages} />
        </Grid>
      )}
      {isLoading && (
        <Grid item container justifyContent={'center'}>
          <CircularProgress />
        </Grid>
      )}
      {!isLoading && !isAddMode && (
        <>
          <Grid item xs={12}>
            <Stack spacing={1}>
              {paymentMethods.map((account) => {
                if (account.paymentType?.toLowerCase() === 'ach') {
                  return (
                    <ACHAccount
                      key={account.paymentMethodId}
                      account={account}
                      updateAccount={updateAccount}
                      deleteAccount={deleteAccount}
                      deleteOption
                      editOption
                    />
                  );
                }
              })}
            </Stack>
          </Grid>
          {!(
            paymentGateway === 'paytrace' &&
            paymentMethods.filter((method) => method.paymentType?.toLowerCase() === 'ach').length > 0
          ) && ( // if paytrace and more than 0 ach payment methods, do not show button
            <Grid item xs={12}>
              <Tooltip title="">
                <Button id="AddNewBankAccount" variant="contained" onClick={handleClickAddAccount}>
                  Add New Bank Account
                </Button>
              </Tooltip>
            </Grid>
          )}
        </>
      )}
      {!isLoading && isAddMode && (
        // isAddMode
        <Grid item xs={12}>
          <ACHEditAccount updateAccount={addAccount} onDone={() => setIsAddMode(false)} />
        </Grid>
      )}
    </Grid>
  );
};

ACHPaymentInfo.propTypes = {
  customerId: PropTypes.any,
};

export default ACHPaymentInfo;
